import { formatIsoDateLong } from '@/app/utils/formatDate';
import type { TemplateVersion, TemplateVersionStatus, TemplateVersionSummary } from '../types/documentTemplates.types';

export const UNKNOWN_USER = 'Unknown user';

export const STATUS_LABELS: Record<TemplateVersionStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  superseded: 'Superseded',
};

export const STATUS_BADGE_VARIANTS = {
  draft: 'warning',
  published: 'success',
  superseded: 'secondary',
} as const satisfies Record<TemplateVersionStatus, string>;

export const actorLabel = (name: string | null): string => name || UNKNOWN_USER;

export const formatDateTime = (iso: string | null): string => (iso ? new Date(iso).toLocaleString() : '—');

export const formatDay = (iso: string | null): string => (iso ? formatIsoDateLong(iso) : '—');

export const draftSubtitle = (
  draft: Pick<TemplateVersion, 'updatedAt' | 'createdAt' | 'updatedByName' | 'createdByName'>,
  revision: number,
  supersedesVersionNumber: number | null,
): string => {
  const saved = `Revision ${revision} · saved ${formatDay(draft.updatedAt ?? draft.createdAt)} by ${actorLabel(draft.updatedByName ?? draft.createdByName)}`;
  return supersedesVersionNumber === null ? saved : `${saved} · replaces v${supersedesVersionNumber} when published`;
};

export const versionActivity = (version: TemplateVersionSummary): string => {
  if (version.status === 'draft') {
    const edited = formatDay(version.updatedAt ?? version.createdAt);
    return `Revision ${version.revision} · edited ${edited} by ${actorLabel(version.updatedByName ?? version.createdByName)}`;
  }
  return version.publishedAt
    ? `${formatDay(version.publishedAt)} by ${actorLabel(version.publishedByName)}`
    : `Created ${formatDay(version.createdAt)} by ${actorLabel(version.createdByName)}`;
};

export const defaultVersionId = (versions: TemplateVersionSummary[]): string | null =>
  (versions.find((version) => version.status === 'published') ?? versions[0])?.id ?? null;
